import { GetServerSidePropsContext } from 'next'
import React, { useEffect } from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { useRouter } from 'next/router'

import {
  MainLayout,
  MainLayoutContent,
  MainLayoutSidebar
} from 'src/features/layouts'
import {
  portfolioApi,
  PORTFOLIO_PROJECT_LIST_KEY,
  useQueryProfilePortfolioProjectList
} from 'src/features/portfolio'
import {
  profileCompanyApi,
  useProfileCompanyDetailsFields,
  useOwnerProfileCompany,
  useQueryProfileCompanyById,
  useQuerySimilarCompanies,
  ProfileCompanyDetails
} from 'src/features/profile-company/common'
import {
  ProfileDetailsCardRead,
  ProfileReviewForm,
  PROFILE_DETAILS_BUTTON
} from 'src/features/profiles'
import {
  profilesApi,
  PROFILES_KEY,
  PROFILE_REVIEWS_KEY,
  PROFILE_TYPE
} from 'src/features/profiles/common'
import { SERVICES_URL, theme, useTranslation } from '@plandi/common'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import { SimilarSlider } from 'src/features/SimilarSlider/slider-specialists-and-compaines/similar-slider'
import { useMediaQuery } from '@material-ui/core'
import { TRANSLATE, sharedApi } from 'src/shared'

export async function getServerSideProps({
  params,
  locale
}: GetServerSidePropsContext) {
  const queryClient = new QueryClient()

  const profileId = params?.profileId ? Number(params.profileId) : undefined

  if (profileId) {
    await Promise.allSettled([
      queryClient.prefetchQuery(
        [PROFILES_KEY, PROFILE_TYPE.COMPANY, profileId, locale],
        () => profileCompanyApi.getProfileCompanyById(profileId, locale ?? 'en')
      ),
      queryClient.prefetchQuery(
        [PORTFOLIO_PROJECT_LIST_KEY, profileId, {}, locale],
        () =>
          portfolioApi.getProfilePortfolioProjectList(locale ?? 'en', profileId)
      ),
      queryClient.prefetchQuery([PROFILE_REVIEWS_KEY, profileId, locale], () =>
        profilesApi.getProfileReviews(profileId, locale ?? 'en')
      ),
      queryClient.prefetchQuery(TRANSLATE, () =>
        sharedApi.getTranslate(locale ?? 'en')
      )
    ])
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      locale: locale
    }
  }
}

const ResumeProfilePage = (props: { locale: string }) => {
  const router = useRouter()
  const { data: translate } = useTranslation()
  const isOwnerProfile = useOwnerProfileCompany()
  const detailsFields = useProfileCompanyDetailsFields()
  const isDesktop = useMediaQuery(theme.breakpoints.up(960))
  const url = SERVICES_URL
  const locale = props.locale

  const profileId = router.query.profileId
    ? Number(router.query.profileId)
    : undefined

  const { data: profileCompany } = useQueryProfileCompanyById(profileId)
  const { data: projects } = useQueryProfilePortfolioProjectList()

  const {
    data: similarCompanies,
    refetch,
    isFetching
  } = useQuerySimilarCompanies(profileId)

  useEffect(() => {
    refetch()
  }, [profileId, refetch])

  const isVisiblePortfolioButton =
    (projects && projects.length > 0) || isOwnerProfile

  const buttonVariants = isVisiblePortfolioButton
    ? isOwnerProfile
      ? [PROFILE_DETAILS_BUTTON.EDIT, PROFILE_DETAILS_BUTTON.PORTFOLIO]
      : projects?.length
      ? [PROFILE_DETAILS_BUTTON.MESSAGE, PROFILE_DETAILS_BUTTON.PORTFOLIO]
      : [PROFILE_DETAILS_BUTTON.MESSAGE]
    : [PROFILE_DETAILS_BUTTON.MESSAGE]

  return (
    <MainLayout
      withFooter
      title={profileCompany?.name}
      description={profileCompany?.about}
      mainContainerStyles={{ display: 'initial' }}
    >
      <div>
        <BreadCrumbs
          breadcrumbInfo={[
            {
              title: translate?.['job']?.['title2'] ?? 'Фриланс и Работа',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/applicants`
                  : `${url.job}/applicants`
            },
            {
              title:
                translate?.['job-site']?.['header']?.['companies'] ??
                'Компании',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/companies`
                  : `${url.job}/companies`
            },
            {
              title: `${profileCompany?.name}`,
              href: ''
            }
          ]}
        />
        <div style={isDesktop ? { display: 'flex' } : {}}>
          <MainLayoutSidebar withSticky size="large">
            <ProfileCompanyDetails
              buttonVariants={buttonVariants}
              translate={translate}
              locale={props.locale}
              {...profileCompany}
            />
            {!isOwnerProfile && (
              <ProfileReviewForm
                reviewType="company"
                isReviewed={profileCompany?.isReviewed}
                profileId={profileCompany?.id}
                translate={translate}
                locale={props.locale}
              />
            )}
          </MainLayoutSidebar>
          <MainLayoutContent>
            <ProfileDetailsCardRead details={detailsFields} />
          </MainLayoutContent>
        </div>

        {similarCompanies?.length && !isFetching && (
          <SimilarSlider
            title={
              translate?.['job-site']?.['similar-companies'] ??
              'Похожие компании'
            }
            sliderData={similarCompanies}
            type="company"
            translate={translate}
            locale={props.locale}
          />
        )}
      </div>
    </MainLayout>
  )
}

export default ResumeProfilePage
